/* istanbul ignore file */
import React from 'react';

import { useHistory } from 'react-router-dom';

import { get as getConstants } from '@lumapps/constants';
import { useDispatch, useStore } from '@lumapps/redux/react';

import { RouterApi, OnAfterRouteHook, OnBeforeRouteHook } from '../api';
import { AppId } from '../constants';
import { isRedirectionBlocked, currentRouteSelector } from '../ducks/selectors';
import { actions as routerActions } from '../ducks/slice';
import { Context, routerApi, angularApi } from '../routers';
import { Route } from '../types';
import { createUrl, isAdminUrl } from '../utils';

const { isLegacyContext, applicationId } = getConstants();

const onBeforeRouteHooks: OnBeforeRouteHook[] = [];
const onAfterRouteHooks: OnAfterRouteHook[] = [];

export const runOnBeforeHooks = (
    to: Route,
    from: Route,
    hooks: OnBeforeRouteHook[],
    appId: AppId = applicationId,
): { shouldRedirect: boolean; shouldRedirectTo?: Route } => {
    let shouldRedirect = true;
    let shouldRedirectTo;
    /*
     * We loop through the onBeforeRoute hooks until one of them
     * cancels the redirection (by calling next(false)) or redirects
     * to another path (by calling next('/path')).
     */
    for (let i = 0; i < hooks.length && (shouldRedirect || shouldRedirectTo); i += 1) {
        const hook = hooks[i];

        // eslint-disable-next-line no-loop-func
        hook(to, from, (res) => {
            if (res === false) {
                shouldRedirect = false;
            }

            if (typeof res === 'object') {
                shouldRedirectTo = res;
            }

            if (typeof res === 'string') {
                shouldRedirectTo = {
                    path: res,
                    appId,
                };
            }
        });
    }

    if (shouldRedirectTo) {
        shouldRedirect = true;
    }

    return { shouldRedirect, shouldRedirectTo };
};

/* istanbul ignore next */
export const useRouter = (): RouterApi => {
    const history = useHistory();
    const currentContext = React.useContext(Context);
    const { getRedirect, getUrl: contextGetUrl } = currentContext;
    const store = useStore<BaseStore>();

    const dispatch = useDispatch();

    const redirect = React.useMemo<RouterApi['redirect']>(() => {
        // Angular router
        if (isLegacyContext) {
            return angularApi.redirect;
        }

        // React router
        const defaultRedirect: RouterApi['redirect'] = (
            route,
            { routerData, openInNewTab, replace, keepFocus, ignoreBlockedRedirection } = {},
        ) => {
            const currentState = store.getState();
            const isRoutingBlocked = isRedirectionBlocked(currentState);
            const currentRoute = currentRouteSelector(currentState);

            const { shouldRedirect, shouldRedirectTo } = runOnBeforeHooks(
                route,
                currentRoute as Route,
                onBeforeRouteHooks,
            );

            if (shouldRedirect) {
                // On redirect, store current location into router state
                const routeWithPreviousPath = shouldRedirectTo || {
                    ...route,
                    state: {
                        previousPath: history?.location?.pathname,
                        ...route.state,
                    },
                };

                // Store router data.
                if (routerData) {
                    dispatch(routerActions.setData(routerData));
                }

                if (!openInNewTab && isRoutingBlocked && !ignoreBlockedRedirection) {
                    // If the routing is blocked, we must set the redirection information in order
                    // to continue the redirection if the user unblocks it.
                    // Once the route is set, the BlockedRedirectionDialog will open.
                    dispatch(routerActions.setBlockedRedirection({ route, history }));
                } else {
                    routerApi.redirect(routeWithPreviousPath, history, { openInNewTab, replace, keepFocus });

                    /**
                     * If the redirection block was ignore, propagate that on redux so that the next navigation works
                     * as expected
                     */
                    if (ignoreBlockedRedirection) {
                        dispatch(routerActions.unblockRedirection());
                    }
                }
            }
        };

        // Web components router, or any app that wraps the default redirect
        if (getRedirect) {
            // eslint-disable-next-line deprecation/deprecation
            return getRedirect({ store, history, redirect: defaultRedirect, getUrl: createUrl });
        }

        return defaultRedirect;
    }, [dispatch, getRedirect, history, store]);

    const currentUrl = window.location.pathname;

    const getUrl = React.useMemo<RouterApi['getUrl']>(() => {
        if (contextGetUrl) {
            // eslint-disable-next-line deprecation/deprecation
            return contextGetUrl({ store, history, redirect, getUrl: createUrl });
        }
        // eslint-disable-next-line deprecation/deprecation
        return createUrl;
    }, [contextGetUrl, history, redirect, store]);

    return {
        registerOnBeforeRedirect: (callback: OnBeforeRouteHook) => {
            onBeforeRouteHooks.push(callback);
        },
        registerOnAfterRedirect: (callback: OnAfterRouteHook) => {
            onAfterRouteHooks.push(callback);
        },
        unregisterOnAfterRedirect: (callback: OnAfterRouteHook) => {
            const index = onAfterRouteHooks.indexOf(callback);
            if (index > -1) {
                onAfterRouteHooks.splice(index, 1);
            }
        },
        unregisterOnBeforeRedirect: (callback: OnBeforeRouteHook) => {
            const index = onBeforeRouteHooks.indexOf(callback);
            if (index > -1) {
                onBeforeRouteHooks.splice(index, 1);
            }
        },
        onAfterRoute: (to, from) => {
            onAfterRouteHooks.forEach((hook) => {
                hook(to, from);
            });
        },
        getUrl,
        redirect,
        context: {
            ...currentContext,
            isBackOffice: isAdminUrl(currentUrl),
        },
    };
};
